import { renew, storeGrant, type Grant, type Session } from "./auth.ts";

/**
 * Keeping a tab's account session fresh (P1.4-T2): a timer that trades the refresh token for a new session token a
 * minute before the old one expires, stores each grant it is given, and says when the session can no longer be
 * renewed. The session token is what the page presents in `hello`; the refresh token is the sign-in behind it.
 *
 * Only a refusal ends the sign-in (the P1.9-T15 review): the gateway answers 401 when the refresh token has
 * expired, was ended by a logout or is not for the character. A refresh that got no answer, or an answer the
 * server may mend (a server error, a busy server, a limit, a body the page cannot read), keeps the grant as it is
 * and is tried again after `RETRY_FIRST_MS`, doubling to `RETRY_CAP_MS`, for as long as it takes, past the session
 * token's own expiry: the gateway checks the session token only at `hello`, so a player in the world keeps
 * playing through an outage of the account endpoints alone, and a lost connection's Reconnect is not turned into
 * Log in by a gateway that is down. The cap is the knob for the load a long outage puts on the gateway once back:
 * one refresh a minute from each tab that failed. Refreshes are spread by each session's own expiry, so the
 * retries carry no jitter.
 */

/**
 * Why a kept-fresh session ended: its refresh token no longer works, or the gateway answered that its
 * account needs a name, its family name or its first character's.
 */
export type SessionEnd = "expired" | "naming";

/**
 * How long before a session token expires the page asks for the next one.
 */
export const REFRESH_MARGIN_MS = 60_000;

/**
 * The shortest wait before a refresh, so a session token already close to its expiry is not asked for again at
 * once and in a loop.
 */
export const MIN_REFRESH_DELAY_MS = 15_000;

/**
 * The wait before trying a failed refresh again for the first time.
 */
export const RETRY_FIRST_MS = 2_000;

/**
 * The longest wait between tries of a refresh that keeps failing.
 */
export const RETRY_CAP_MS = 60_000;

/**
 * What keeping a session fresh runs on: the page's timer, its clock and the tab's grant store, unless a test hands
 * in its own.
 */
export interface FreshDeps {
  /**
   * Runs a callback after a delay.
   *
   * @param delayMs The delay in milliseconds.
   * @param run The callback.
   * @returns A function that cancels it.
   */
  readonly schedule: (delayMs: number, run: () => void) => () => void;
  /**
   * The time now, in milliseconds since the epoch, as a session's `expiresAt` counts it.
   *
   * @returns The time.
   */
  readonly now: () => number;
  /**
   * Stores or forgets the tab's grant.
   *
   * @param grant The grant, or null to forget it.
   */
  readonly store: (grant: Grant | null) => void;
}

/**
 * The page's own timer, clock and grant store.
 */
const PAGE: FreshDeps = {
  schedule: (delayMs, run) => {
    const timer = window.setTimeout(run, delayMs);
    return () => window.clearTimeout(timer);
  },
  now: () => Date.now(),
  store: storeGrant,
};

/**
 * How long to wait before refreshing a session: until a minute before its token expires, and never less than
 * `MIN_REFRESH_DELAY_MS`.
 *
 * @param session The session held.
 * @param now The time now.
 * @returns The wait in milliseconds.
 * @example
 * refreshDelay({ ...session, expiresAt: now + 600_000 }, now);
 * // 540_000
 */
export function refreshDelay(session: Session, now: number): number {
  return Math.max(MIN_REFRESH_DELAY_MS, session.expiresAt - now - REFRESH_MARGIN_MS);
}

/**
 * How long to wait before trying a refresh again after it has failed a number of times in a row: `RETRY_FIRST_MS`
 * after the first, doubling each time, and never more than `RETRY_CAP_MS`.
 *
 * @param failures The failures in a row, 1 or more.
 * @returns The wait in milliseconds.
 * @example
 * [1, 2, 3, 6, 7].map(retryDelay);
 * // [2_000, 4_000, 8_000, 60_000, 60_000]
 */
export function retryDelay(failures: number): number {
  return Math.min(RETRY_CAP_MS, RETRY_FIRST_MS * 2 ** (failures - 1));
}

/**
 * Keeps a session token fresh, refreshing a minute before it expires and storing each new one. Each refresh
 * names the character the session plays, so the token stays that character's. A refresh answered with a
 * naming grant ends the session and stores that grant, so the next reload reaches the name step; a refusal
 * forgets the grant and ends it as expired; any other failure keeps the grant and tries again with backoff
 * (`retryDelay`) until a refresh is answered.
 *
 * @param base The account origin.
 * @param initial The session to start from.
 * @param onSession Called with each refreshed session.
 * @param onEnd Called once when the session can no longer be refreshed, with why.
 * @param deps The timer, clock and grant store; the page's own unless a test hands in others.
 * @returns A function that stops the timer, and any retry waiting.
 */
export function keepFresh(base: string, initial: Session, onSession: (session: Session) => void, onEnd: (why: SessionEnd) => void, deps: FreshDeps = PAGE): () => void {
  let cancel: (() => void) | null = null;
  let stopped = false;
  /**
   * Asks for the next session token, and acts on what came of it.
   *
   * @param session The session now held.
   * @param failures How many refreshes of it have failed in a row before this one.
   */
  const attempt = (session: Session, failures: number): void => {
    void renew(base, session.refreshToken, session.characterId).then((outcome) => {
      if (stopped) {
        return;
      }
      if (outcome.kind === "failed") {
        cancel = deps.schedule(retryDelay(failures + 1), () => attempt(session, failures + 1));
        return;
      }
      if (outcome.kind === "refused") {
        deps.store(null);
        onEnd("expired");
        return;
      }
      deps.store(outcome.grant);
      if (outcome.grant.kind === "naming") {
        onEnd("naming");
        return;
      }
      onSession(outcome.grant.session);
      wait(outcome.grant.session);
    });
  };
  /**
   * Waits for the time to refresh one session.
   *
   * @param session The session now held.
   */
  const wait = (session: Session): void => {
    cancel = deps.schedule(refreshDelay(session, deps.now()), () => attempt(session, 0));
  };
  wait(initial);
  return () => {
    stopped = true;
    cancel?.();
  };
}

/**
 * What a refresh made before a join came to (P1.9-T32): the session to present, no answer from the gateway, or a
 * sign-in that ended.
 */
export type LapsedOutcome = { readonly kind: "ready"; readonly session: Session } | { readonly kind: "failed" } | { readonly kind: "ended"; readonly why: SessionEnd };

/**
 * Whether a session token has lapsed, or is inside the margin before it does, so a join presenting it could be refused.
 * The timer in `keepFresh` renews a token before this, and keeps trying through an outage; a token that is lapsed
 * nevertheless is one that outage outlived.
 *
 * @param session The session held.
 * @param now The time now.
 * @returns True when the token has `REFRESH_MARGIN_MS` or less left.
 */
export function lapsed(session: Session, now: number): boolean {
  return session.expiresAt - now <= REFRESH_MARGIN_MS;
}

/**
 * Refreshes a session once before a join, for a token that lapsed in an outage, so the gateway meets a token it can
 * tell is good: `hello` cannot tell an expired token from a revoked one, and ends the sign-in on either. A refresh
 * that got no answer is `failed`, and the join is not made with the lapsed token; a refusal forgets the grant and
 * ends the sign-in as `keepFresh` does, and a naming grant is stored and ends it as naming.
 *
 * @param base The account origin.
 * @param session The session held.
 * @param deps The grant store; the page's own unless a test hands in another.
 * @returns The new session, no answer, or why the sign-in ended.
 */
export async function renewLapsed(base: string, session: Session, deps: FreshDeps = PAGE): Promise<LapsedOutcome> {
  const outcome = await renew(base, session.refreshToken, session.characterId);
  if (outcome.kind === "failed") {
    return { kind: "failed" };
  }
  if (outcome.kind === "refused") {
    deps.store(null);
    return { kind: "ended", why: "expired" };
  }
  deps.store(outcome.grant);
  return outcome.grant.kind === "naming" ? { kind: "ended", why: "naming" } : { kind: "ready", session: outcome.grant.session };
}
